import { useMutation } from "@apollo/client";

import {
  BULK_DELETE_OEM_CUSTOMERS_MUTATION,
  CHECK_VALID_FACILITY_USER_MUTATION,
  CREATE_OEM_CUSTOMER_V2_MUTATION,
  CREATE_OEM_FACILITY_USER_MUTATION,
  DELETE_OEM_CUSTOMER_MUTATION,
  DELETE_OEM_FACILITY_USER_MUTATION,
  LIST_OWN_OEM_ALL_TICKETS_QUERY,
  LIST_OWN_OEM_FACILITY_USERS_QUERY,
  UPDATE_OEM_FACILITY_USER_MUTATION,
  Customer,
  UPDATE_OEM_CUSTOMER_MUTATION,
  ASSIGN_COMPANIES_TO_PARENT_MUTATION,
  ASSIGN_PARENTS_TO_COMPANY_MUTATION,
  UNASSIGN_COMPANY_FROM_PARENT_MUTATION,
} from "~/api";
import { errorsHandler } from "~/transformers";
import { evictHierarchyCaches } from "~/utils/graphql";

export const checkIfFacilityUser = () => {
  const [checkValidFacilityUser, { error = {}, loading }] = useMutation(
    CHECK_VALID_FACILITY_USER_MUTATION,
  );

  return {
    mutation: (args) =>
      new Promise((resolve, reject) => {
        const { userId } = args || {};
        return checkValidFacilityUser({
          variables: {
            userId,
          },
          skip: !userId,
        })
          .then((response) => {
            const { data: { checkValidFacilityUser: value } = {} } =
              response || {};

            if (value === false) {
              throw new Error(value);
            }
            resolve(value);
          })
          .catch((err) => {
            reject(err);
          });
      }),
    loading,
    error,
  };
};

export const removeFacilityUser = () => {
  const [remove, { loading, error }] = useMutation(
    DELETE_OEM_FACILITY_USER_MUTATION,
  );

  return {
    loading,
    error,
    removeUser: async (id) =>
      remove({
        variables: {
          id,
        },
        skip: !id,
        update(cache) {
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "getOwnOemCustomerById",
          });
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listOwnOemFacilityUsers",
          });
          cache.gc();
        },
      }),
  };
};

export const updateFacilityUser = (facilityId) => {
  const [update, { loading, error }] = useMutation(
    UPDATE_OEM_FACILITY_USER_MUTATION,
  );

  return {
    loading,
    error,
    updateUser: async (input) =>
      update({
        variables: { input },
        skip: !input?._id,
        update(cache, { data: { updateOemFacilityUser: updatedDataRef } }) {
          try {
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "getOwnOemCustomerById",
            });
            cache.gc();

            const { listOwnOemFacilityUsers } = cache.readQuery({
              query: LIST_OWN_OEM_FACILITY_USERS_QUERY,
              variables: { facilityId },
            });
            const cloned = [...listOwnOemFacilityUsers];

            const index = cloned.findIndex((item) => item._id === input._id);
            cloned.splice(index, 1, updatedDataRef);

            cache.writeQuery({
              query: LIST_OWN_OEM_FACILITY_USERS_QUERY,
              variables: { facilityId },
              data: {
                listOwnOemFacilityUsers: cloned,
              },
            });
          } catch (error) {
            console.log("error", error);
          }
        },
      }),
  };
};

export const createFacilityUser = (facilityId) => {
  const [create, { loading, error }] = useMutation(
    CREATE_OEM_FACILITY_USER_MUTATION,
  );

  return {
    loading,
    error,
    createUser: async (input) =>
      create({
        variables: { input },
        skip: !input?.facilityId,
        update(cache, { data: { createOemFacilityUser: ref } }) {
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "getOwnOemCustomerById",
          });
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listOwnOemFacilityUsers",
          });
          cache.gc();
        },
      }),
  };
};

export const deleteFacility = () => {
  const [deleteCustomer, { loading, error }] = useMutation(
    DELETE_OEM_CUSTOMER_MUTATION,
  );

  return {
    loading,
    error,
    removeFacility: async (customerId) =>
      deleteCustomer({
        variables: { customerId },
        skip: !customerId,
        update(cache) {
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listAllOwnOemCustomers",
          });
          cache.gc();

          // updating cache: Ticket List
          try {
            const ticketListCache = cache.readQuery({
              query: LIST_OWN_OEM_ALL_TICKETS_QUERY,
              variables: {},
            });
            if (!ticketListCache) return;

            const cloned = [...ticketListCache.listOwnOemAllTickets];
            const newTicketList = cloned.map((ticket) => {
              const t = { ...ticket };
              if (t.facility && t.facility._id === customerId) {
                t.facility = null;
              }
              return t;
            });

            cache.writeQuery({
              query: LIST_OWN_OEM_ALL_TICKETS_QUERY,
              variables: {},
              data: { listOwnOemAllTickets: newTicketList },
            });
          } catch (error) {
            console.error("[Facility Delete][Ticket List]", error);
          }
        },
      }),
  };
};

export const useCreateOemCustomer = () => {
  const [create, { loading, error }] = useMutation(
    CREATE_OEM_CUSTOMER_V2_MUTATION,
  );

  return {
    loading,
    error,
    createOemCustomer: async (input) =>
      create({
        variables: { input },
        update: (cache, { data: { createOemCustomerV2: ref } }) => {
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listAllOwnOemCustomers",
          });
          cache.gc();
        },
      }),
  };
};

export const useAssignCompaniesToParent = () => {
  const [assign, { loading, error }] = useMutation(
    ASSIGN_COMPANIES_TO_PARENT_MUTATION,
  );

  return {
    loading,
    error,
    mutation: ({ companyIds, parentId }) =>
      new Promise((resolve, reject) =>
        assign({
          variables: { input: { companyIds, parentId } },
          update(cache) {
            evictHierarchyCaches(cache, [parentId, ...(companyIds || [])]);
          },
        })
          .then(resolve)
          .catch((err) => reject(errorsHandler(err))),
      ),
  };
};

export const useAssignParentsToCompany = () => {
  const [assign, { loading, error }] = useMutation(
    ASSIGN_PARENTS_TO_COMPANY_MUTATION,
  );

  return {
    loading,
    error,
    mutation: ({ companyId, parentIds }) =>
      new Promise((resolve, reject) =>
        assign({
          variables: { input: { companyId, parentIds } },
          update(cache) {
            evictHierarchyCaches(cache, [companyId, ...(parentIds || [])]);
          },
        })
          .then(resolve)
          .catch((err) => reject(errorsHandler(err))),
      ),
  };
};

export const useUnassignCompanyFromParent = () => {
  const [unassign, { loading, error }] = useMutation(
    UNASSIGN_COMPANY_FROM_PARENT_MUTATION,
  );

  return {
    loading,
    error,
    mutation: ({ companyId, parentId }) =>
      new Promise((resolve, reject) =>
        unassign({
          variables: { input: { companyId, parentId } },
          update(cache) {
            evictHierarchyCaches(cache, [companyId, parentId]);
          },
        })
          .then(resolve)
          .catch((err) => reject(errorsHandler(err))),
      ),
  };
};

export const useUpdateOemCustomer = () => {
  const [updateCustomer, { loading, error }] = useMutation(
    UPDATE_OEM_CUSTOMER_MUTATION,
  );

  return {
    loading,
    error,
    updateOemCustomer: async (input, { skipCacheUpdate = false } = {}) =>
      updateCustomer({
        variables: { input },
        update(cache, { data: { updateOemCustomer: ref } }) {
          if (skipCacheUpdate) {
            return;
          }
          cache.modify({
            id: cache.identify({ __typename: "Customer", ref: ref._id }),
            fields: {
              getOwnOemCustomerById() {
                const newRef = cache.writeFragment({
                  data: ref,
                  fragment: Customer.fragments.CustomerFullData,
                  fragmentName: "CustomerFullData",
                });

                return newRef;
              },
            },
          });
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listOwnOemAllTickets",
          });
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listAllOwnOemMachines",
          });
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listAllOwnOemAssets",
          });
          // Evict after delay to allow the MongoDB trigger to create the activity log
          setTimeout(() => {
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "listOwnOemActivityLogs",
            });
            cache.gc();
          }, 2000);
        },
      }),
  };
};

export const useBulkDeleteCustomers = () => {
  const [bulkDelete] = useMutation(BULK_DELETE_OEM_CUSTOMERS_MUTATION);

  const bulkDeleteCustomers = async (
    customerIds,
    deleteSubCompanies = false,
  ) => {
    const { data } = await bulkDelete({
      variables: { customerIds, deleteSubCompanies },
      update(cache) {
        ["listAllOwnOemCustomers", "listOwnOemKanbanCustomers"].forEach(
          (fieldName) => cache.evict({ id: "ROOT_QUERY", fieldName }),
        );
        cache.gc();
      },
    });
    return data.bulkDeleteOemCustomers;
  };

  return { bulkDeleteCustomers };
};
